
import { useEffect, useRef } from 'react';

export default function HomePage() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Schema.org JSON-LD の追加
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": "飲み処まなさんち",
      "description": "本千葉駅徒歩1分。昭和レトロな雰囲気と手づくりの温もりがある串焼き居酒屋・飲み処まなさんち。昼飲み、ホルモン煮込み、串焼きが自慢です。",
      "url": import.meta.env.VITE_SITE_URL,
      "telephone": "090-2163-5037",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "長洲1丁目24−1 エスカイヤ本千葉第一114",
        "addressLocality": "千葉市中央区",
        "addressRegion": "千葉県",
        "postalCode": "260-0854",
        "addressCountry": "JP"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 35.6020618,
        "longitude": 140.12123150000002
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "11:00",
        "closes": "03:30"
      },
      "servesCuisine": "Japanese",
      "priceRange": "$$",
      "acceptsReservations": true
    });
    document.head.appendChild(script);

    // FAQPage Schema.org JSON-LD の追加
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "営業時間は何時から何時までですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "11:00から27:30まで営業しております。定休日は日曜日です。"
          }
        },
        {
          "@type": "Question", 
          "name": "予約は必要ですか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "予約をお勧めしております。お電話（090-2163-5037）でご予約ください。"
          }
        },
        {
          "@type": "Question",
          "name": "本千葉駅からどのくらいの距離ですか？",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "本千葉駅から徒歩1分の好立地にございます。"
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // スクロールアニメーションの設定
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // アニメーション対象の要素を監視
    const animatedElements = document.querySelectorAll('.scroll-fade-in, .scroll-scale-in');
    animatedElements.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // クリーンアップ
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      {/* ヘッダー */}
      <header className="bg-amber-100/80 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-amber-900" style={{ fontFamily: '"Hiragino Kaku Gothic ProN", "ヒラギノ角ゴ ProN W3", "Yu Gothic Medium", "游ゴシック Medium", YuGothic, "游ゴシック体", "Noto Sans JP", "メイリオ", Meiryo, sans-serif' }}>
              飲み処まなさんち
            </h1>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-amber-800 hover:text-amber-600 transition-colors">お店について</a>
              <a href="#commitment" className="text-amber-800 hover:text-amber-600 transition-colors">こだわり</a>
              <a href="#video" className="text-amber-800 hover:text-amber-600 transition-colors">動画</a>
              <a href="#access" className="text-amber-800 hover:text-amber-600 transition-colors">アクセス</a>
            </nav>
          </div>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <iframe 
            src="https://www.youtube.com/embed/TflockiG0Pg?autoplay=1&mute=1&loop=1&playlist=Isq0J5yipS0&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1"
            title="飲み処まなさんち紹介動画"
            className="w-full h-full object-cover"
            style={{ minWidth: '100%', minHeight: '100%' }}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6 animate-fade-in-up">
            飲み処まなさんち
          </h2>
          <p className="text-base md:text-xl mb-6 md:mb-8 animate-fade-in-up whitespace-nowrap" style={{ animationDelay: '0.3s' }}>
            昭和の温もりが息づく、心安らぐひととき
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <a 
              href="tel:090-2163-5037" 
              className="bg-amber-600 hover:bg-amber-700 text-white px-6 md:px-8 py-2 md:py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              電話で予約する
            </a>
          </div>
        </div>
      </section>

      {/* お店についてセクション */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 scroll-fade-in">
              <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-6">お店について</h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
                「ただいま」って言いたくなる、本千葉駅の寄り道。
                香ばしい音と匂い、笑顔が迎えてくれる場所。
                ここは、お腹も心も満たされる昭和レトロな串焼き店です。
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="scroll-fade-in">
                <img 
                  src="https://static.readdy.ai/image/757e1f70611b4564a8c0cd90bed89c15/8b900b014e3ca39b52f7b0fbb74c9230.jpeg"
                  alt="飲み処まなさんちの店内"
                  className="w-full h-80 object-cover rounded-lg shadow-lg"
                />
              </div>
              <div className="scroll-scale-in">
                <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">心温まる空間で、<br />特別なひとときを</h3>
                <div className="space-y-4 text-gray-700">
                  <p>
                    昭和レトロな雰囲気の中で、心からリラックスできる時間をご過ごしください。
                    店内は温かみのある木の温もりと、やわらかな照明に包まれています。
                  </p>
                  <p>
                    一人でも、大切な人とでも、どなたでも気軽にお立ち寄りいただける、アットホームな雰囲気を大切にしています。
                  </p>
                  <p>
                    小腹が空いた時、心がそわそわした時、いつでもお気軽にお越しください。
                    飲み処まなさんちが、あなたの第二の我が家になれば幸いです。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* こだわりセクション */}
      <section id="commitment" className="py-20 bg-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 scroll-fade-in">
              <h2 className="text-4xl font-bold text-amber-900 mb-6">
                大切にしている<br />3つの想い
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center scroll-scale-in">
                <div className="bg-white rounded-lg p-8 shadow-lg h-full">
                  <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Fresh%20raw%20meat%20being%20cut%20with%20sharp%20knife%20on%20wooden%20cutting%20board%20in%20professional%20kitchen%2C%20close-up%20shot%20of%20butcher%20hands%20slicing%20premium%20beef%2C%20clean%20bright%20lighting%2C%20food%20preparation%20scene%2C%20restaurant%20kitchen%20atmosphere&width=80&height=80&seq=meat-cutting-1&orientation=squarish"
                      alt="新鮮な肉を切る"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-amber-900 mb-4">心を込めたおもてなし</h3>
                  <p className="text-gray-700 leading-relaxed">
                    お客様一人ひとりを大切に、心からのおもてなしでお迎えします。
                    まるで実家に帰ったような、温かい雰囲気を心がけています。
                  </p>
                </div>
              </div>
              
              <div className="text-center scroll-scale-in" style={{ animationDelay: '0.2s' }}>
                <div className="bg-white rounded-lg p-8 shadow-lg h-full">
                  <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Delicious%20yakitori%20skewers%20being%20brushed%20with%20glossy%20tare%20sauce%2C%20grilled%20chicken%20on%20bamboo%20sticks%20with%20golden%20brown%20color%2C%20chef%20hand%20applying%20sweet%20soy%20glaze%2C%20close-up%20shot%20of%20Japanese%20kushiyaki%20preparation%2C%20appetizing%20food%20photography%20with%20warm%20lighting%2C%20restaurant%20kitchen%20scene&width=80&height=80&seq=yakitori-tare-2&orientation=squarish"
                      alt="タレを塗る串焼き"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-amber-900 mb-4">厳選された食材</h3>
                  <p className="text-gray-700 leading-relaxed">
                    地元の新鮮な食材を中心に、季節の味覚を大切にした料理をご提供。
                    素材の味を活かした、やさしい味付けが自慢です。
                  </p>
                </div>
              </div>
              
              <div className="text-center scroll-scale-in" style={{ animationDelay: '0.4s' }}>
                <div className="bg-white rounded-lg p-8 shadow-lg h-full">
                  <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Happy%20friends%20cheerfully%20toasting%20with%20beer%20glasses%20in%20cozy%20Japanese%20izakaya%20restaurant%2C%20multiple%20hands%20holding%20beer%20mugs%20clinking%20together%2C%20warm%20golden%20lighting%2C%20joyful%20atmosphere%2C%20people%20laughing%20and%20celebrating%2C%20traditional%20wooden%20interior%20background%2C%20authentic%20Japanese%20drinking%20culture%20scene&width=80&height=80&seq=beer-cheers-3&orientation=squarish"
                      alt="ビールで乾杯する人々"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-amber-900 mb-4">駅チカ×昼飲み</h3>
                  <p className="text-gray-700 leading-relaxed">
                    本千葉駅から徒歩すぐ、迷わず来られる串焼き店。昼から一杯飲めるお店として、常連さんにも愛されています。仕事前でも、休みの日でも、ふらっと寄れる気軽な場所です。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 動画セクション */}
      <section id="video" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="scroll-fade-in">
              <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-12">
                小腹と心がそわそわしたら<br />
                飲み処まなさんち
              </h2>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe 
                  src="https://www.youtube.com/embed/5bUR0DOnnoQ"
                  title="飲み処まなさんち紹介動画"
                  className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* アクセス・お問い合わせセクション */}
      <section id="access" className="py-20 bg-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16 scroll-fade-in">
              <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-6">
                アクセス<br />
                お問い合わせ
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="scroll-scale-in">
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-amber-900 mb-6">店舗情報</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-map-pin-fill text-amber-600 text-xl mt-1"></i>
                      <div>
                        <p className="font-semibold text-gray-900">住所</p>
                        <p className="text-gray-700">〒260-0854<br />千葉県千葉市中央区長洲1丁目24−1<br />エスカイヤ本千葉第一114</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-phone-fill text-amber-600 text-xl mt-1"></i>
                      <div>
                        <p className="font-semibold text-gray-900">電話番号</p>
                        <p className="text-gray-700">090-2163-5037</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-time-fill text-amber-600 text-xl mt-1"></i>
                      <div>
                        <p className="font-semibold text-gray-900">営業時間</p>
                        <p className="text-gray-700">
                          11:00 - 27:30<br />
                          定休日: 日曜日
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <a 
                      href="tel:090-2163-5037" 
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer inline-block text-center"
                    >
                      <i className="ri-phone-fill mr-2"></i>
                      電話で予約する
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="scroll-scale-in" style={{ animationDelay: '0.2s' }}>
                <div className="bg-white rounded-lg overflow-hidden shadow-lg h-full">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3244.040732651268!2d140.12123150000002!3d35.6020618!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60229b07074e2867%3A0x4de669cfbcf06efb!2z6aOy44G_5YemIOOBvuOBquOBleOCk-OBoSDosZrkuLI!5e0!3m2!1sja!2sjp!4v1762241697827!5m2!1sja!2sjp" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0, minHeight: '400px' }}
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="飲み処まなさんち 地図"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-amber-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="scroll-fade-in">
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Hiragino Kaku Gothic ProN", "ヒラギノ角ゴ ProN W3", "Yu Gothic Medium", "游ゴシック Medium", YuGothic, "游ゴシック体", "Noto Sans JP", "メイリオ", Meiryo, sans-serif' }}>
                  飲み処まなさんち
                </h3>
                <p className="text-amber-100 leading-relaxed">
                  昭和の温もりが息づく、心安らぐひととき。
                  小腹と心がそわそわしたら、いつでもお気軽にお越しください。
                </p>
              </div>
              
              <div className="scroll-fade-in" style={{ animationDelay: '0.2s' }}>
                <h4 className="text-lg font-semibold mb-4">店舗情報</h4>
                <div className="space-y-2 text-amber-100">
                  <p>〒260-0854</p>
                  <p>千葉県千葉市中央区長洲1丁目24−1</p>
                  <p>エスカイヤ本千葉第一114</p>
                  <p>TEL: 090-2163-5037</p>
                </div>
              </div>
              
              <div className="scroll-fade-in" style={{ animationDelay: '0.4s' }}>
                <h4 className="text-lg font-semibold mb-4">営業時間</h4>
                <div className="space-y-2 text-amber-100">
                  <p>11:00 - 27:30</p>
                  <p>定休日: 日曜日</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-amber-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-amber-200 text-sm">
                © 2024 飲み処まなさんち. All rights reserved.
              </p>
              <div className="flex space-x-4 mt-4 md:mt-0">
                <a 
                  href="https://www.instagram.com/nomi_dokoro_manasanchi/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-200 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-fill text-2xl"></i>
                </a>
              </div>
              <div className="mt-4 md:mt-0">
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-amber-200 hover:text-white transition-colors text-sm cursor-pointer"
                >
                  Website Builder
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
